import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n";

const EASE = [0.85, 0, 0.15, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden pb-[0.1em] -mb-[0.1em] ${className}`}>
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function RotatingBadge() {
  return (
    <div className="absolute -bottom-8 -left-8 w-32 h-32 md:w-36 md:h-36 z-10" data-testid="hero-badge">
      <div className="absolute inset-0 rounded-full bg-brand border-2 border-topacio-ink shadow-[4px_4px_0_0_#111]" />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-spin-slow">
        <defs>
          <path id="badge-circle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
        </defs>
        <text className="fill-topacio-ink" style={{ fontSize: "7.5px", fontWeight: 800, letterSpacing: "1.4px" }}>
          <textPath href="#badge-circle">TOPACIO · TENERIFE · DESDE 1991 ·</textPath>
        </text>
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-hand text-3xl text-topacio-red -rotate-6 select-none">
        91
      </span>
    </div>
  );
}

export default function Hero({ onBook }) {
  const { t } = useLang();
  const [videoOk, setVideoOk] = useState(true);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-24 md:pb-32 bg-topacio-sand">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="eyebrow mb-6 flex items-center gap-3"
          >
            <span className="inline-block w-10 h-0.5 bg-topacio-red" />
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-topacio-ink uppercase leading-[1.02]">
            <MaskedLine delay={0.15}>{t.hero.titleA}</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="inline-block bg-brand border-2 border-topacio-ink px-3 shadow-[5px_5px_0_0_#111] -rotate-1 mt-2">
                {t.hero.titleB}
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: "easeOut" }}
            className="mt-8 text-base md:text-lg text-topacio-faded max-w-lg leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: "easeOut" }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#fleet" data-testid="hero-fleet-btn" className="btn-primary">{t.hero.cta1}</a>
            <button data-testid="hero-book-btn" onClick={() => onBook(null)} className="btn-secondary">{t.hero.cta2}</button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative mt-6 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 4 }} animate={{ opacity: 1, y: 0, rotate: 2 }}
            transition={{ duration: 1, delay: 0.45, ease: EASE }}
            className="relative card-brutal overflow-hidden hover:rotate-0 transition-transform duration-500"
          >
            <div className="aspect-[16/10] lg:aspect-[4/5] overflow-hidden">
              {videoOk ? (
                <motion.video
                  autoPlay muted loop playsInline
                  poster="/hero-riders.jpg"
                  onError={() => setVideoOk(false)}
                  style={{ y: imgY }}
                  className="w-full h-full object-cover scale-110 will-change-transform"
                >
                  <source src="/hero-riders.webm" type="video/webm" />
                  <source src="/hero-riders.mp4" type="video/mp4" />
                </motion.video>
              ) : (
                <motion.img
                  src="/hero-riders.jpg"
                  alt="Clientes de Rent a Moto Topacio rodando en moto por las carreteras de Tenerife"
                  style={{ y: imgY }}
                  className="w-full h-full object-cover scale-110 will-change-transform"
                />
              )}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }} animate={{ opacity: 1, scale: 1, rotate: -2 }}
            transition={{ duration: 0.7, delay: 1, ease: "backOut" }}
          >
            <RotatingBadge />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.15 }}
            className="absolute -top-5 -right-4 bg-topacio-red text-white border-2 border-topacio-ink shadow-[4px_4px_0_0_#111] px-5 py-2.5 rotate-3 z-10"
          >
            <span className="font-hand text-2xl">¡Nos vemos en la carretera!</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
